import { createPinia, PiniaVuePlugin } from 'pinia'
import Vue from 'vue'

import App from '@/App.vue'
import api from '@/logic/api'
import { i18n } from '@/logic/i18n'
import { initializeChatThreads, isChatEnabled, listenToChatEvents } from '@/logic/init/chatSetup'
import { appSetup, init } from '@/logic/init/init'
import { initOnlineStatusSetup } from '@/logic/init/onlineStatusSetup'
import { relaunchApi } from '@/logic/relaunchApi'
import constantsMixin from '@/mixins/constants'
import AuthAPI from '@/modules/auth/api/auth'
import router from '@/router'
import store from '@/store'

Vue.use(PiniaVuePlugin)
const pinia = createPinia()

function updateConfigFromId(storeInstance) {
  AuthAPI.getProfileConfig(`id:${storeInstance.getters['auth/config'].id}`).then((config) => {
    storeInstance.dispatch('auth/setConfig', config)
  })
}

function updateConfigFromSlug(storeInstance) {
  AuthAPI.getProfileConfig(`slug:${storeInstance.getters['auth/config'].slug}`).then((config) => {
    storeInstance.dispatch('auth/setConfig', config)
  })
}

function updateConfig(storeInstance) {
  if (storeInstance.getters['auth/isLoggedIn']) {
    updateConfigFromId(storeInstance)
  } else {
    updateConfigFromSlug(storeInstance)
  }
}

async function startApp(config = null) {
  if (config !== null && config.status !== 404) {
    store.dispatch('auth/setConfig', config)
  }
  api.setup(store, i18n, router)
  relaunchApi.setup(store, i18n, router)
  await init(store, i18n, router)
  if ((config && config.status !== 404) || store.getters['auth/hasConfig']) {
    await appSetup(store, i18n, router, pinia)
  }

  Vue.mixin(constantsMixin)

  new Vue({
    i18n,
    render: (h) => h(App),
    router,
    store,
    pinia,
  }).$mount('#app')

  // Handle navigation requests coming from the service worker (e.g., notification clicks)
  if ('serviceWorker' in navigator) {
    if (!window.__kl_sw_message_listener_attached) {
      window.__kl_sw_message_listener_attached = true
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event && event.data && event.data.event === 'notificationClick') {
          const link = event.data.link
          if (!link) {
            return
          }
          try {
            const url = new URL(link, window.location.origin)
            if (url.origin === window.location.origin) {
              const routePath = `${url.pathname}${url.search}${url.hash}`
              router.push(routePath)
            } else {
              // External link: open in a new tab to avoid disrupting the SPA
              window.open(link, '_blank')
            }
          } catch (e) {
            // If URL parsing fails, fall back to internal navigation for app-relative paths
            if (link.startsWith('/')) {
              router.push(link)
            } else {
              window.open(link, '_blank')
            }
          }
        }
      })
    }
  }

  if (config?.status === 404) {
    if (!store.getters['auth/hasConfig']) {
      // We don't have a config yet, so the user has to select their app first
      router.replace({
        name: 'auth.select-app',
      })
    } else {
      updateConfig(store)
    }
  }
  if (isChatEnabled()) {
    initializeChatThreads()
    listenToChatEvents()
  }
  initOnlineStatusSetup()
}

Vue.config.productionTip = false
if (window.klConfig !== null) {
  // Config is already loaded, start the app
  startApp(window.klConfig)
} else if (store.getters['auth/hasConfig']) {
  // We already have the config in the store, start right away and update the config as soon as we get it
  startApp()
  window.addEventListener('configloaded', () => {
    if (window.klConfig && window.klConfig.status !== 404) {
      store.dispatch('auth/setConfig', window.klConfig)
    } else {
      updateConfig(store)
    }
  })
} else {
  // We have no config stored yet, so wait until its loaded
  window.addEventListener('configloaded', () => {
    startApp(window.klConfig)
  })
}

// Handle navigation requests coming from the service worker (e.g., notification clicks)
if ('serviceWorker' in navigator) {
  if (!window.klSwMessageListenerAttached) {
    window.klSwMessageListenerAttached = true
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event && event.data && event.data.event === 'notificationClick') {
        const link = event.data.link
        if (!link) {
          return
        }
        try {
          const url = new URL(link, window.location.origin)
          if (url.origin === window.location.origin) {
            const routePath = `${url.pathname}${url.search}${url.hash}`
            router.push(routePath)
          } else {
            // External link: open in a new tab to avoid disrupting the SPA
            window.open(link, '_blank')
          }
        } catch (e) {
          // If URL parsing fails, fall back to internal navigation for app-relative paths
          if (link.startsWith('/')) {
            router.push(link)
          } else {
            window.open(link, '_blank')
          }
        }
      }
    })
  }
}
